import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Text } from '@pixi/react';
import gsap from 'gsap';

import { Spine } from '../../../../../core/classes/spine';
import { isMobile } from '../../../../../core/helpers/isMobile';
import { isoStandard } from '../../../../../core/helpers/isoStandard';
import { exchangeAmount } from '../../../../../core/helpers/exchangeAmountByRate';
import { parseNumberPrecisionByDoteCount } from '../../../../../core/helpers/parseNumberByPrecision';

import { jackPotStore } from '../../../../../store/jackPotStore';
import { userInfoStore } from '../../../../../store/userInfoStore';

import JackPotNumbersItemSymbol from '../jackPotNumberItemSymbol';

let timeLine = null;
const JackPotSceneNumberItemContent = observer(
  ({ x, y, id, type, image, winIcon, containerName }) => {
    const { jackpotSceneAmounts } = jackPotStore;
    const amountRef = useRef(null);
    const amountBackgroundRef = useRef(null);
    const [currentTimeJackPotWinAmount, setCurrentTimeJackPotWinAmount] = useState(0);
    const { isWinMoveAndScaleSymbolComplete } = jackPotStore;

    const amount = useMemo(() => {
      return jackpotSceneAmounts[type] ? exchangeAmount(jackpotSceneAmounts[type]) : '';
    }, [jackpotSceneAmounts, type]);

    const isWinIcon = useMemo(() => {
      return id === winIcon?.jackpotCard;
    }, [winIcon, id]);

    useEffect(() => {
      setCurrentTimeJackPotWinAmount(amount);
    }, [amount]);

    useEffect(() => {
      const jackpotAmountBoardSpine = new Spine({ key: 'jackpotAmountBoard' });
      jackpotAmountBoardSpine.play('shine', true);
      amountBackgroundRef.current.addChild(jackpotAmountBoardSpine);
      if (winIcon?.jackpotCard === id && isWinMoveAndScaleSymbolComplete) {
        jackpotAmountBoardSpine.play('win', true);
        jackpotAmountBoardSpine.addListener(
          'complete',
          () => {
            jackPotStore.setIsShowJackPotWinPopup(true);
          },
          null,
          'win'
        );
      }
    }, [id, isWinMoveAndScaleSymbolComplete, winIcon]);

    useEffect(() => {
      if (!isWinIcon) {
        return;
      }
      timeLine && timeLine.kill();
      timeLine = gsap.timeline();

      const isInteger = amount === Math.floor(amount);
      timeLine.to(
        { value: currentTimeJackPotWinAmount },
        {
          value: amount,
          duration: 5,
          snap: { textContent: isInteger ? 1 : 1 / 10 ** userInfoStore.precision },
          stagger: {
            each: 0.1,
            onUpdate: function () {
              const newAmount = +parseNumberPrecisionByDoteCount(
                +this.targets()[0].value,
                1,
                userInfoStore.precision
              );
              setCurrentTimeJackPotWinAmount(newAmount);
            },
            onComplete: function () {
              timeLine.kill();
            },
          },
        }
      );
    }, [amount, isWinIcon]);

    const detectFontSize = useCallback((newAmount) => {
      return isMobile()
        ? `${newAmount}`.length > 14
          ? 16
          : 18
        : `${newAmount}`.length > 14
        ? 22
        : 24;
    }, []);

    return (
      <Container x={x} y={y} name={containerName}>
        <Container
          y={isMobile() ? 25 : 50}
          ref={amountBackgroundRef}
          name='JackPotAmountBoardContainer'
        />
        <JackPotNumbersItemSymbol image={image} containerName='JackPotWinSymbolContainer' />
        <Text
          anchor={0.5}
          ref={amountRef}
          y={isMobile() ? 30 : 55}
          text={
            currentTimeJackPotWinAmount
              ? isoStandard(currentTimeJackPotWinAmount)
              : isoStandard(amount)
          }
          style={{
            fill: '#FFFFFF',
            fontWeight: 600,
            fontSize: detectFontSize(
              currentTimeJackPotWinAmount
                ? isoStandard(currentTimeJackPotWinAmount)
                : isoStandard(amount)
            ),
            _fontFamily: 'poppinsMedium',
          }}
        />
      </Container>
    );
  }
);

export default JackPotSceneNumberItemContent;
